import { ReactSelectOption } from "@/lib/types";
import { FieldErrors } from "react-hook-form";

export const isBrowser = () => {
  return typeof window !== "undefined";
};

export const getLeadSource = () => {
  const defaultSource = "HomePage";
  if (typeof window === "undefined") return defaultSource;
  const leadSource = window.location.pathname;
  return leadSource || defaultSource;
};

export const getUTMParam = (param: string) => {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  return params.get(param) || "";
};

export const checkFormError = (errors: FieldErrors, id: string) => {
  if (errors[id] && errors[id].message) {
    return errors[id].message as string;
  }
  return "";
};

export const formatPhoneNumber = (phone: string) => {
  return phone ? phone.replace(/^(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3") : "";
};

export const downloadBase64File = (base64: string, fileName: string) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  // Convert to a Blob
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray]);

  // Create a download link
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();

  // Clean up the URL Object
  URL.revokeObjectURL(link.href);
};

export const previewPdfBase64FileInNewTab = (base64: string) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);

  // Open in new tab - will only work if browser supports the MIME type
  const newTab = window.open(blobUrl, "_blank");
  if (!newTab) {
    alert("Pop-up blocked. Please allow pop-ups for this site.");
  }
  // Clean up the URL Object
  URL.revokeObjectURL(blobUrl);
};

export const checkBooleanExists = (value: any) => {
  return value === true || value === false;
};

export const inchesToHeight = (value: string) => {
  const numInches = parseInt(value, 10);

  if (isNaN(numInches)) {
    return "";
  }

  const feet = Math.floor(numInches / 12);
  const remainderInches = numInches % 12;
  return `${feet}' ${remainderInches}"`;
};

export const sortNotes = (a: any, b: any) => {
  // Check if notes are pinned
  const aIsPinned = a.isPinned;
  const bIsPinned = b.isPinned;

  // If one note is pinned and the other is not, the pinned one should come first
  if (aIsPinned && !bIsPinned) {
    return -1;
  }
  if (!aIsPinned && bIsPinned) {
    return 1;
  }

  // Determine if the notes are incomplete tasks
  const aIsIncompleteTask = a.taskId !== null && a.completedBy === null;
  const bIsIncompleteTask = b.taskId !== null && b.completedBy === null;

  // If both are incomplete tasks, sort them by due date
  if (aIsIncompleteTask && bIsIncompleteTask) {
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  }

  // If only one is an incomplete task, it should come first
  if (aIsIncompleteTask && !bIsIncompleteTask) {
    return -1; // a comes before b
  }
  if (!aIsIncompleteTask && bIsIncompleteTask) {
    return 1; // b comes before a
  }

  const aSortKey = a.completedBy ? "completedOn" : "createdDate";
  const bSortKey = b.completedBy ? "completedOn" : "createdDate";

  // If neither or both are non-tasks or completed tasks, sort them chronologically
  return new Date(b[bSortKey]).getTime() - new Date(a[aSortKey]).getTime();
};

/* React Select / Filtering */

export const findValueForSelect = (
  options: ReactSelectOption[],
  value: any,
  isMulti?: boolean
) => {
  return isMulti
    ? options.filter((option) => value.includes(option.value))
    : options.find((option) => option.value === value) || null;
};

export const parseFilterValue = (value: string) => {
  return JSON.parse(value || "[]");
};

export const stringifyFilterValue = (selected: ReactSelectOption[]) => {
  return selected ? JSON.stringify(selected.map((sel) => sel.value)) : null;
};
